import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FinishProductService } from 'app/service/finish-product.service';
import { MasterService } from 'app/service/master.service';
import { UserService } from 'app/service/user.service';
import { environment } from 'environments/environment';
import {
  FP_BULK_MAX_ROWS,
  FP_BULK_STATUS_OPTIONS,
  FpBulkAssignment,
  FpBulkImportItem,
  FpBulkLookups,
  FpBulkParseResult,
  FpBulkPreviewRow,
} from './super-admin-finish-product-bulk-upload.models';
import {
  applyFpCsvMasterMappingsToRows,
  applyGlobalStatusToFpRows,
  downloadSuperAdminFinishProductTemplate,
  mapFpMetadataToLookups,
  rebuildFpBulkPayload,
  refreshFpBulkParseCounts,
  validateFpBulkCsv,
  validateFpBulkExcel,
} from './super-admin-finish-product-bulk-upload.util';
import {
  enrichFpBulkRowsFromIngredients,
  extractFpIngredientTaxonomyFromDetail,
} from './super-admin-finish-product-bulk-ingredient-enricher';
import { SuperAdminFinishProductBulkUploadRowEditorComponent } from './super-admin-finish-product-bulk-upload-row-editor.component';

export type FpBulkTemplateFormat = 'csv' | 'xlsx';

@Component({
  selector: 'app-super-admin-finish-product-bulk-upload-modal',
  templateUrl: './super-admin-finish-product-bulk-upload-modal.component.html',
  styleUrls: ['./super-admin-finish-product-bulk-upload-modal.component.scss'],
})
export class SuperAdminFinishProductBulkUploadModalComponent implements OnInit, AfterViewInit {
  readonly maxRows = FP_BULK_MAX_ROWS;
  readonly statusOptions = FP_BULK_STATUS_OPTIONS.map((s) => ({ value: s, label: s }));
  readonly assignmentPriorities = ['High', 'Medium', 'Low'];

  @ViewChild('assignModalTpl') assignModalTpl!: TemplateRef<unknown>;

  step: 'upload' | 'preview' = 'upload';
  loadingLookups = false;
  parsing = false;
  enrichingIngredients = false;
  enrichProgress = '';
  fileName = '';
  fileError = '';
  parseResult: FpBulkParseResult | null = null;
  globalStatus = 'Draft';
  lookups: FpBulkLookups = {
    healthColorCodes: [],
    dietaryPreferences: [],
    dataSources: [],
    databaseSources: [],
    countries: [],
    tastes: [],
    textures: [],
    flavours: [],
    aromas: [],
    continents: [],
    occasions: [],
    locations: [],
    seasons: [],
    cuisines: [],
    allergens: [],
    allergenFree: [],
    intolerances: [],
    intoleranceFree: [],
    fssaiCategories: [],
    eStoreCategories: [],
    units: [],
    reworkSuggestions: [],
    nutrients: [],
    nutrientGroups: [],
    nutrientTags: [],
    claimTypes: [],
    claimTags: [],
    productBenefits: [],
    productBenefitTags: [],
    productTypes: [],
  };

  assignMode: 'maker-and-checker' | 'checker-only' = 'maker-and-checker';
  assignTargetRows: FpBulkPreviewRow[] = [];
  assignDialogIsBulk = false;
  selectedMakerEmail = '';
  selectedCheckerEmail = '';
  selectedDueDate = '';
  selectedPriority = 'Medium';
  selectedNotes = '';
  makerList: any[] = [];
  checkerList: any[] = [];
  isMakerLoading = false;
  isCheckerLoading = false;
  private assignModalRef: NgbModalRef | null = null;
  private statusBeforeAssign = 'Draft';
  private lastNonAssignGlobalStatus = 'Draft';
  private assigningGlobal = false;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private masterService: MasterService,
    private finishProductService: FinishProductService,
    private userService: UserService
  ) {}

  get subtitle(): string {
    return this.step === 'upload'
      ? 'Download the template, fill text fields in CSV or Excel, then upload. Dropdown fields are configured per row in preview.'
      : 'Review rows, configure dropdowns, set status (default Draft), then import.';
  }

  get validCount(): number {
    return this.parseResult?.validCount || 0;
  }

  get errorCount(): number {
    return this.parseResult?.errorCount || 0;
  }

  get totalCount(): number {
    return this.parseResult?.totalCount || 0;
  }

  get assignTargetProduct(): FpBulkPreviewRow | null {
    return !this.assignDialogIsBulk && this.assignTargetRows.length === 1
      ? this.assignTargetRows[0]
      : null;
  }

  async ngOnInit(): Promise<void> {
    await this.loadLookups();
    this.scheduleLucideIconRender();
  }

  ngAfterViewInit(): void {
    this.scheduleLucideIconRender();
  }

  downloadTemplate(format: FpBulkTemplateFormat = 'csv'): void {
    downloadSuperAdminFinishProductTemplate(format);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const isCsv = lowerName.endsWith('.csv');
    const isExcel = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls');
    if (!isCsv && !isExcel) {
      this.fileError = 'Please upload a valid CSV or Excel (.xlsx / .xls) file.';
      input.value = '';
      return;
    }

    this.fileError = '';
    this.fileName = file.name;
    this.parsing = true;

    const applyParseResult = async (result: FpBulkParseResult): Promise<void> => {
      this.parseResult = result;
      if (this.parseResult.fileError) {
        this.fileError = this.parseResult.fileError;
        this.step = 'upload';
        return;
      }
      if (!this.parseResult.totalCount) {
        this.fileError = 'No data rows found in the file.';
        this.step = 'upload';
        return;
      }
      applyFpCsvMasterMappingsToRows(this.parseResult.rows, this.lookups);
      applyGlobalStatusToFpRows(this.parseResult.rows, this.globalStatus);
      // Promote ingredient drafts → rows before master enrich.
      this.refreshAllRows();
      this.step = 'preview';
      await this.enrichIngredientsFromMaster();
      this.refreshAllRows();
    };

    const reader = new FileReader();
    reader.onload = () => {
      void (async () => {
        try {
          if (isCsv) {
            await applyParseResult(validateFpBulkCsv(String(reader.result || '')));
          } else {
            await applyParseResult(validateFpBulkExcel(reader.result as ArrayBuffer));
          }
        } catch (err) {
          console.error('Failed to parse finish product bulk file', err);
          this.fileError = 'Failed to parse CSV or Excel file.';
          this.step = 'upload';
        } finally {
          this.parsing = false;
          this.scheduleLucideIconRender();
        }
      })();
    };
    reader.onerror = () => {
      this.parsing = false;
      this.fileError = 'Failed to read file.';
    };
    if (isCsv) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
    input.value = '';
  }

  backToUpload(): void {
    this.step = 'upload';
    this.parseResult = null;
    this.fileName = '';
    this.fileError = '';
    this.scheduleLucideIconRender();
  }

  onGlobalStatusChange(): void {
    if (!this.parseResult?.rows?.length) return;
    const next = this.globalStatus;

    if (this.isAssignmentStatus(next)) {
      this.statusBeforeAssign = this.lastNonAssignGlobalStatus || 'Draft';
      applyGlobalStatusToFpRows(this.parseResult.rows, next);
      this.refreshAllRows();
      void this.openAssignDialog(this.parseResult.rows, next, true);
      return;
    }

    this.lastNonAssignGlobalStatus = next;
    applyGlobalStatusToFpRows(this.parseResult.rows, next);
    this.refreshAllRows();
  }

  onRowStatusChange(row: FpBulkPreviewRow): void {
    const next = row.selections.status;
    if (this.isAssignmentStatus(next)) {
      this.statusBeforeAssign = 'Draft';
      if (
        next === 'Assigned to Maker' &&
        (!row.selections.assignment?.makerEmail || !row.selections.assignment?.checkerEmail)
      ) {
        row.selections.assignment = null;
      }
      if (next === 'Assigned to Checker' && !row.selections.assignment?.checkerEmail) {
        row.selections.assignment = null;
      }
      rebuildFpBulkPayload(row, this.lookups);
      refreshFpBulkParseCounts(this.parseResult!);
      void this.openAssignDialog([row], next, false);
      return;
    }

    row.selections.assignment = null;
    rebuildFpBulkPayload(row, this.lookups);
    refreshFpBulkParseCounts(this.parseResult!);
  }

  private isAssignmentStatus(status: string): boolean {
    return status === 'Assigned to Maker' || status === 'Assigned to Checker';
  }

  private async openAssignDialog(
    rows: FpBulkPreviewRow[],
    status: string,
    isBulk: boolean
  ): Promise<void> {
    this.assignMode = status === 'Assigned to Checker' ? 'checker-only' : 'maker-and-checker';
    this.assignTargetRows = rows;
    this.assignDialogIsBulk = isBulk || rows.length > 1;
    this.assigningGlobal = isBulk;

    const existing = rows[0]?.selections?.assignment;
    this.selectedMakerEmail = existing?.makerEmail || '';
    this.selectedCheckerEmail = existing?.checkerEmail || '';
    this.selectedDueDate = existing?.dueDate || '';
    this.selectedPriority = existing?.priority || 'Medium';
    this.selectedNotes = existing?.notes || '';

    if (this.assignMode === 'maker-and-checker') {
      void this.loadRoleUserList('maker');
      void this.loadRoleUserList('checker');
    } else {
      void this.loadRoleUserList('checker');
    }

    try {
      this.assignModalRef?.dismiss('reopen');
    } catch {
      /* noop */
    }

    this.assignModalRef = this.modalService.open(this.assignModalTpl, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: true,
      windowClass: 'recipe-assign-modal-window saru-assign-window',
    });
    this.scheduleLucideIconRender();

    this.assignModalRef.result.then(
      () => {
        this.assignModalRef = null;
      },
      (reason) => {
        if (reason !== 'assigned') {
          this.onAssignCancelled();
        }
        this.assignModalRef = null;
      }
    );
  }

  private onAssignCancelled(): void {
    if (this.assigningGlobal && this.parseResult?.rows?.length) {
      this.globalStatus = this.statusBeforeAssign || 'Draft';
      applyGlobalStatusToFpRows(this.parseResult.rows, this.globalStatus);
      this.refreshAllRows();
    } else if (this.assignTargetRows.length === 1) {
      const row = this.assignTargetRows[0];
      if (!row.selections.assignment) {
        row.selections.status = this.statusBeforeAssign || 'Draft';
        row.selections.assignment = null;
        rebuildFpBulkPayload(row, this.lookups);
        refreshFpBulkParseCounts(this.parseResult!);
      }
    }
    this.assignTargetRows = [];
    this.assigningGlobal = false;
  }

  confirmAssignment(modal?: { close: (r?: string) => void }): void {
    if (this.assignMode === 'maker-and-checker') {
      if (!this.selectedMakerEmail || !this.selectedCheckerEmail) return;
    } else if (!this.selectedCheckerEmail) {
      return;
    }

    const assignment: FpBulkAssignment = {
      mode: this.assignMode,
      makerEmail: this.assignMode === 'maker-and-checker' ? this.selectedMakerEmail : null,
      checkerEmail: this.selectedCheckerEmail,
      dueDate: this.selectedDueDate || null,
      priority: this.selectedPriority || 'Medium',
      notes: this.selectedNotes || null,
    };

    for (const row of this.assignTargetRows) {
      row.selections.assignment = { ...assignment };
      row.selections.status =
        this.assignMode === 'maker-and-checker' ? 'Assigned to Maker' : 'Assigned to Checker';
      rebuildFpBulkPayload(row, this.lookups);
    }

    if (this.assigningGlobal) {
      this.globalStatus =
        this.assignMode === 'maker-and-checker' ? 'Assigned to Maker' : 'Assigned to Checker';
    }

    refreshFpBulkParseCounts(this.parseResult!);
    modal?.close('assigned');
    this.assignModalRef = null;
    this.assignTargetRows = [];
    this.assigningGlobal = false;
    this.scheduleLucideIconRender();
  }

  closeAssignDialog(modal?: { dismiss: (r?: string) => void }): void {
    modal?.dismiss('cancel');
  }

  getAssignTargetCount(): number {
    return this.assignTargetRows.length;
  }

  private async loadRoleUserList(role: 'maker' | 'checker'): Promise<void> {
    const isMaker = role === 'maker';
    if (isMaker) {
      this.isMakerLoading = true;
      this.makerList = [];
    } else {
      this.isCheckerLoading = true;
      this.checkerList = [];
    }

    try {
      const res: any = await this.userService.getUsers({
        pageNo: 1,
        pageSize: 200,
        searchString: '',
        roleId: isMaker ? 5 : 4,
      });
      if (res && res.status === 0 && res.response) {
        const users = res.response.userList || res.response.data || res.response || [];
        if (isMaker) this.makerList = users;
        else this.checkerList = users;
      }
    } catch (error) {
      console.error(`Failed to load ${role} list:`, error);
    } finally {
      if (isMaker) this.isMakerLoading = false;
      else this.isCheckerLoading = false;
    }
  }

  async onRowImageSelected(event: Event, row: FpBulkPreviewRow): Promise<void> {
    await this.uploadRowAsset(event, row, 'frontImage', 'frontImageUploading');
  }

  async onRowCertificateSelected(event: Event, row: FpBulkPreviewRow): Promise<void> {
    await this.uploadRowAsset(event, row, 'authorizedCertificateImage', 'authorizedCertificateUploading');
  }

  async onRowLicenseSelected(event: Event, row: FpBulkPreviewRow): Promise<void> {
    await this.uploadRowAsset(event, row, 'authorizedLicenseImage', 'authorizedLicenseUploading');
  }

  private async uploadRowAsset(
    event: Event,
    row: FpBulkPreviewRow,
    valueKey: 'frontImage' | 'authorizedCertificateImage' | 'authorizedLicenseImage',
    loadingKey: 'frontImageUploading' | 'authorizedCertificateUploading' | 'authorizedLicenseUploading'
  ): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      this.fileError = `Invalid image type for row #${row.rowNumber}. Use PNG, JPEG, or WebP.`;
      input.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.fileError = `Image too large for row #${row.rowNumber}. Max 5MB.`;
      input.value = '';
      return;
    }

    this.fileError = '';
    row.selections[loadingKey] = true;
    try {
      const res: any = await this.finishProductService.uploadProductImages([file]);
      const payload = res?.response ?? res?.Response ?? res?.data ?? res;
      const urls =
        payload?.urls ||
        payload?.imageUrls ||
        payload?.files ||
        (Array.isArray(payload) ? payload : null) ||
        [];
      const url = Array.isArray(urls) ? urls[0] : typeof urls === 'string' ? urls : null;
      if (url) {
        row.selections[valueKey] = String(url);
        rebuildFpBulkPayload(row, this.lookups);
        refreshFpBulkParseCounts(this.parseResult!);
      } else {
        this.fileError = `Failed to upload image for row #${row.rowNumber}.`;
      }
    } catch (err) {
      console.error('Row image upload failed', err);
      this.fileError = `Failed to upload image for row #${row.rowNumber}.`;
    } finally {
      row.selections[loadingKey] = false;
      input.value = '';
      this.scheduleLucideIconRender();
    }
  }

  removeRowImage(row: FpBulkPreviewRow): void {
    row.selections.frontImage = null;
    rebuildFpBulkPayload(row, this.lookups);
    refreshFpBulkParseCounts(this.parseResult!);
    this.scheduleLucideIconRender();
  }

  removeRowCertificate(row: FpBulkPreviewRow): void {
    row.selections.authorizedCertificateImage = null;
    rebuildFpBulkPayload(row, this.lookups);
    refreshFpBulkParseCounts(this.parseResult!);
  }

  removeRowLicense(row: FpBulkPreviewRow): void {
    row.selections.authorizedLicenseImage = null;
    rebuildFpBulkPayload(row, this.lookups);
    refreshFpBulkParseCounts(this.parseResult!);
  }

  getRowImageUrl(row: FpBulkPreviewRow): string | null {
    return this.resolveUploadUrl(row?.selections?.frontImage);
  }

  getRowCertificateUrl(row: FpBulkPreviewRow): string | null {
    return this.resolveUploadUrl(row?.selections?.authorizedCertificateImage);
  }

  getRowLicenseUrl(row: FpBulkPreviewRow): string | null {
    return this.resolveUploadUrl(row?.selections?.authorizedLicenseImage);
  }

  getErrorSummary(error?: string | null): string {
    const parts = String(error || '')
      .split(/;\s*/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) return '—';
    if (parts.length === 1) {
      const one = parts[0];
      return one.length > 42 ? one.slice(0, 40) + '…' : one;
    }
    return `${parts.length} issues — hover for details`;
  }

  formatErrorTooltip(error?: string | null): string {
    return String(error || '')
      .split(/;\s*/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p, i) => `${i + 1}. ${p}`)
      .join('\n');
  }

  private resolveUploadUrl(rawValue?: string | null): string | null {
    const raw = String(rawValue || '').trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw) || /^blob:/i.test(raw) || /^data:/i.test(raw)) return raw;
    if (raw.startsWith('//')) return `https:${raw}`;
    if (raw.startsWith('assets/')) return raw;

    const base = (environment.baseUrl || '').replace(/\/$/, '');
    let path = raw.replace(/^\//, '');
    // Bare filename from upload API → serve from /uploads/products/
    if (!path.includes('/') && !path.startsWith('uploads/')) {
      path = `uploads/products/${path}`;
    }
    const encodedPath = path
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');
    return base ? `${base}/${encodedPath}` : `/${encodedPath}`;
  }

  openRowEditor(row: FpBulkPreviewRow): void {
    const modalRef = this.modalService.open(SuperAdminFinishProductBulkUploadRowEditorComponent, {
      size: 'xl',
      backdrop: 'static',
      centered: true,
      windowClass: 'super-admin-bulk-row-editor-window',
    });
    modalRef.componentInstance.row = row;
    modalRef.componentInstance.lookups = this.lookups;

    modalRef.result
      .then((updated: FpBulkPreviewRow) => {
        if (!updated) return;
        rebuildFpBulkPayload(updated, this.lookups);
        refreshFpBulkParseCounts(this.parseResult!);
        if (this.isAssignmentStatus(updated.selections.status) && !updated.selections.assignment) {
          void this.openAssignDialog([updated], updated.selections.status, false);
        }
        this.scheduleLucideIconRender();
      })
      .catch(() => {});
  }

  deleteRow(index: number): void {
    if (!this.parseResult?.rows?.length) return;
    this.parseResult.rows.splice(index, 1);
    this.parseResult.rows.forEach((r, i) => {
      r.rowNumber = i + 1;
    });
    if (!this.parseResult.rows.length) {
      this.backToUpload();
      return;
    }
    this.refreshAllRows();
    this.scheduleLucideIconRender();
  }

  confirmImport(): void {
    const validItems: FpBulkImportItem[] = (this.parseResult?.rows || [])
      .filter((r) => r.valid && r.payload)
      .map((r) => ({
        payload: r.payload as Record<string, unknown>,
        assignment: r.selections.assignment || null,
      }));
    if (!validItems.length) return;
    this.activeModal.close(validItems);
  }

  private refreshAllRows(): void {
    if (!this.parseResult) return;
    this.parseResult.rows.forEach((row) => {
      rebuildFpBulkPayload(row, this.lookups);
    });
    refreshFpBulkParseCounts(this.parseResult);
  }

  /** Resolve ingredient names → master IDs, then FAO / allergen / intolerance taxonomy. */
  private async enrichIngredientsFromMaster(): Promise<void> {
    if (!this.parseResult?.rows?.length) return;
    this.enrichingIngredients = true;
    this.enrichProgress = 'Matching ingredients to master…';
    try {
      await enrichFpBulkRowsFromIngredients(
        this.parseResult.rows,
        {
          bulkResolveIngredients: async (names) => this.resolveIngredientNamesBulk(names),
          getIngredientDetail: async (ingredientId) => {
            const res: any = await this.masterService.getIngredientById(String(ingredientId));
            const detail = res?.data ?? res?.Data ?? res?.response ?? res?.Response ?? res;
            return extractFpIngredientTaxonomyFromDetail(detail);
          },
        },
        (done, total) => {
          this.enrichProgress =
            done === 0
              ? `Resolving ingredients…`
              : done >= total
                ? `Loaded FAO / allergens for ${total} ingredient(s)`
                : `Loading FAO / allergens… ${done}/${total}`;
        }
      );
    } catch (err) {
      console.error('Failed to enrich finish-product bulk ingredients', err);
    } finally {
      this.enrichingIngredients = false;
      this.enrichProgress = '';
      this.scheduleLucideIconRender();
    }
  }

  private async resolveIngredientNamesBulk(
    names: string[]
  ): Promise<Map<string, { id: number; name: string; ingredientRefId?: number | null } | null>> {
    const map = new Map<string, { id: number; name: string; ingredientRefId?: number | null } | null>();
    const unique = Array.from(
      new Set((names || []).map((n) => String(n || '').trim()).filter(Boolean))
    );
    if (!unique.length) return map;

    const chunkSize = 800;
    for (let i = 0; i < unique.length; i += chunkSize) {
      const chunk = unique.slice(i, i + chunkSize);
      const res: any = await this.masterService.resolveIngredientsByNames(chunk);
      const payload = res?.data ?? res?.response ?? res?.Response ?? res;
      const matches = Array.isArray(payload?.matches)
        ? payload.matches
        : Array.isArray(payload?.Matches)
          ? payload.Matches
          : [];

      for (const m of matches) {
        const query = String(m?.queryName ?? m?.QueryName ?? '').trim();
        if (!query) continue;
        const key = query.toLowerCase().replace(/\s+/g, ' ');
        const matched = !!(m?.matched ?? m?.Matched);
        if (!matched) {
          map.set(key, null);
          continue;
        }
        const id = Number(
          m?.id ?? m?.Id ?? m?.ingredientMasterId ?? m?.IngredientMasterId ?? 0
        );
        const refId = Number(m?.ingredientRefId ?? m?.IngredientRefId ?? 0);
        map.set(key, {
          id: Number.isFinite(id) && id > 0 ? id : 0,
          ingredientRefId: Number.isFinite(refId) && refId > 0 ? refId : null,
          name:
            m?.ingredientName ||
            m?.IngredientName ||
            m?.commonName ||
            m?.CommonName ||
            query,
        });
      }
    }
    return map;
  }

  private async loadLookups(): Promise<void> {
    this.loadingLookups = true;
    try {
      const [productMeta, countryMeta, recipeMeta, healthRes, unitsRes]: any[] = await Promise.all([
        this.masterService.getProductMetadata(
          'ingredientItemTypes,recipeLinkSources,claimTypes,claimTags,productBenefits,productBenefitTags,nutrientGroups,nutrientTags,reworkSuggested,fssaiNutrients,fssaiCategories,eStoreCategories,productTypes'
        ),
        this.masterService.getProductMetadata('countries'),
        this.masterService.getRecipeMetadata(
          'seasons,occasions,flavours,textures,aromas,allergens,allergenFree,intolerances,intoleranceFree,dietaryPreferences,tastes,locations,cuisines,cuisineContinents'
        ),
        this.finishProductService.getHealthColorCodes(),
        this.masterService.getIngredientUnitOfMeasurements({
          pageNo: 1,
          pageSize: 1000,
          searchString: '',
        }),
      ]);

      let mapped = mapFpMetadataToLookups(productMeta, countryMeta, recipeMeta);
      const healthList =
        healthRes?.status === 0 && Array.isArray(healthRes.response) ? healthRes.response : [];
      if (healthList.length) {
        mapped = {
          ...mapped,
          healthColorCodes: healthList
            .map((item: any) => ({
              id: String(item.id ?? item.Id ?? ''),
              name: String(
                item.title ?? item.Title ?? item.label ?? item.Label ?? item.name ?? item.Name ?? ''
              ).trim(),
              label: String(item.label ?? item.Label ?? item.title ?? item.Title ?? '').trim(),
              colorCode: String(item.colorCode ?? item.ColorCode ?? item.color ?? '').trim(),
            }))
            .filter((item: any) => item.id && item.name),
        };
      }

      const unitItems =
        unitsRes?.response?.items ||
        unitsRes?.response?.list ||
        unitsRes?.data?.items ||
        unitsRes?.data?.records ||
        (Array.isArray(unitsRes?.response) ? unitsRes.response : []) ||
        [];
      const units = (Array.isArray(unitItems) ? unitItems : [])
        .filter((item: any) => item?.isActive !== false)
        .map((item: any) => ({
          id: String(item.id ?? item.Id ?? ''),
          name: String(item.name ?? item.Name ?? item.code ?? item.Code ?? '').trim(),
        }))
        .filter((u: any) => u.id && u.name);
      if (units.length) {
        mapped = { ...mapped, units };
      }

      // Prefer fssaiNutrients from product metadata when present
      const productRoot = productMeta?.response ?? productMeta?.data ?? productMeta ?? {};
      const fssaiNutrients = productRoot.fssaiNutrients || productRoot.FssaiNutrients || [];
      if (Array.isArray(fssaiNutrients) && fssaiNutrients.length) {
        mapped = {
          ...mapped,
          nutrients: fssaiNutrients
            .map((item: any) => ({
              id: String(item.id ?? item.Id ?? ''),
              name: String(item.name ?? item.Name ?? item.title ?? item.Title ?? '').trim(),
            }))
            .filter((n: any) => n.id && n.name),
        };
      }

      this.lookups = mapped;
    } catch (err) {
      console.error('Failed to load finish product bulk upload lookups', err);
    } finally {
      this.loadingLookups = false;
    }
  }

  private scheduleLucideIconRender(): void {
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      if ((window as any).lucide?.createIcons) {
        (window as any).lucide.createIcons();
      }
    }, 50);
  }
}
